/**
 * Stale-deploy chunk recovery.
 *
 * When we push a new build, the old index.html cached in the browser still
 * references chunk URLs from the previous build (their hashes change every
 * deploy). Navigating to a lazy route fetches a 404 chunk and React Router
 * renders our ErrorBoundary.
 *
 * Two failure surfaces:
 *   1. window-level 'error' events from <script> tags + unhandled
 *      promise rejections from bare `import()` (caught in main.jsx)
 *   2. React Router catches lazy() chunk rejections and never lets them
 *      bubble — so we have to detect chunk errors INSIDE the boundary
 *      and trigger the same recovery flow
 *
 * Recovery budget: up to 3 hard reloads in a 60-second window. Each reload
 * adds a cache-busting query param so any intermediate CDN/proxy/browser
 * cache that's serving the old index.html is forced to re-fetch.
 */

const CHUNK_ERROR_PATTERNS = [
  'loading chunk',
  'loading css chunk',
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'expected a javascript-or-wasm module script',
];

export function isChunkLoadError(error) {
  if (!error) return false;
  const message = String(error?.message || error || '').toLowerCase();
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((p) => message.includes(p));
}

export function attemptHardReload() {
  if(window.__cervicaPublicIntakeOpen)return false;
  try {
    const STATE_KEY = 'cervicaChunkReloadState';
    const raw = sessionStorage.getItem(STATE_KEY);
    const now = Date.now();
    let state = { firstAt: now, attempts: 0 };
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (now - parsed.firstAt < 120_000) state = parsed;
      } catch { /* reset */ }
    }
    // ONE auto-reload per 2-minute window. The earlier "3 attempts in 60s"
    // recipe caused an infinite-flicker loop when each fresh index.html
    // also referenced a chunk that 404'd (rolling deploy mid-state with
    // a stale CloudFront index.html). After this one attempt, surrender
    // to the HTML-level fallback in index.html which shows a manual
    // "Cervica is updating, click to reload" panel.
    if (state.attempts >= 1) {
      return false;
    }
    state.attempts += 1;
    state.lastAt = now;
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state));

    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(now));
    window.location.replace(url.toString());
    return true;
  } catch {
    window.location.reload();
    return true;
  }
}

/**
 * Force a hard reload even if the recovery budget is exhausted — used by
 * the "Reload page" button in the ErrorBoundary, since the user explicitly
 * asked for it. Clears the recovery state on the way out so the next
 * automatic recovery starts fresh.
 */
export function forceHardReload() {
  try {
    sessionStorage.removeItem('cervicaChunkReloadState');
  } catch { /* noop */ }
  const url = new URL(window.location.href);
  url.searchParams.set('_cb', String(Date.now()));
  window.location.replace(url.toString());
}
